import { ESPIONAGE_FLIGHT_LIMIT_MULTIPLIER, FLEETS_MIN_TIME_SECONDS, FLIGHT_DISTANCE_AU_FACTOR, FLIGHT_GALAXY_DISTANCE_FACTOR, FLIGHT_GAS_DISTANCE_DIVISOR, FLIGHT_LIMIT_BASE, FLIGHT_LIMIT_PER_RADAR_LEVEL, FLIGHT_POSITION_DISTANCE_BASE, FLIGHT_POSITION_DISTANCE_FACTOR, FLIGHT_SYSTEM_DISTANCE_BASE, FLIGHT_SYSTEM_DISTANCE_FACTOR, FLIGHT_TIME_BASE_SECONDS, FLIGHT_TIME_FACTOR, RECYCLE_SUN_BASE_SECONDS, SAME_COORDINATE_MISSION_BASE_SECONDS, SPACE_TRIP_GAS_COST, UNFLYABLE_SHIP_IDS, getShipDefinition, } from "@kaeliux/shared";
import { getUpgradedShipStats } from "./ships.js";
/**
 * Pure flight formulas — `docs/game-details/flight_management_system.md`
 * §13-§18 and the §26 formula summary. No I/O, no clocks: timestamps and
 * randomness are always injected by callers, matching the rest of this
 * package. The functions layer recomputes everything here on dispatch;
 * client-side calls are display helpers only (§6.1).
 */
// ---------------------------------------------------------------------------
// Distance (§13)
// ---------------------------------------------------------------------------
/**
 * Internal distance `d` between two coordinates, driven by the first
 * coordinate level that differs. Same coordinate → 0 (special-timed
 * missions only — see {@link calculateMissionFlightSeconds}).
 */
export function calculateFlightDistance(from, to) {
    if (from.galaxy !== to.galaxy) {
        return Math.abs(from.galaxy - to.galaxy) * FLIGHT_GALAXY_DISTANCE_FACTOR;
    }
    if (from.system !== to.system) {
        return FLIGHT_SYSTEM_DISTANCE_BASE + FLIGHT_SYSTEM_DISTANCE_FACTOR * Math.abs(from.system - to.system);
    }
    if (from.position !== to.position) {
        return FLIGHT_POSITION_DISTANCE_BASE + FLIGHT_POSITION_DISTANCE_FACTOR * Math.abs(from.position - to.position);
    }
    return 0;
}
/** Displayed distance in AU (`d × 1000`). */
export function flightDistanceAU(distance) {
    return distance * FLIGHT_DISTANCE_AU_FACTOR;
}
// ---------------------------------------------------------------------------
// Fleet aggregates (§5, §14, §18, §19)
// ---------------------------------------------------------------------------
function shipEntries(ships) {
    return Object.entries(ships)
        .filter((entry) => !!entry[1] && entry[1] > 0)
        .map(([key, count]) => [Number(key), count]);
}
/**
 * Combined science speed multiplier: Astronomy +10%/level, Rocket Fuel and
 * Aerodynamics +15%/level, Wormholes +20%/level (each capped at its
 * science's 15-level max). The bonuses ADD onto one base — stacking them
 * multiplicatively would compound to ~22× at max, far beyond the per-science
 * "up to +X%" phrasing in `sciences.md`.
 */
export function calculateShipSpeedScienceMultiplier(sciences) {
    const capped = (id, perLevel) => Math.min(Math.max(sciences[id] ?? 0, 0), 15) * perLevel;
    return 1
        + capped("astronomy", 0.10)
        + capped("rockets", 0.15)
        + capped("aerodynamics", 0.15)
        + capped("wormholes", 0.20);
}
/**
 * Effective fleet speed: the SLOWEST selected ship's speed after ship
 * upgrades (recycling ship line) and the combined science multiplier (§14: "based
 * on the slowest selected ship after ship speed bonuses"). Returns 0 for an
 * empty manifest or one containing only unflyable ships — callers must
 * reject such fleets before computing a flight time.
 */
export function calculateFleetSpeed(race, ships, sciences = {}, shipUpgrades = {}) {
    const multiplier = calculateShipSpeedScienceMultiplier(sciences);
    let slowest = Infinity;
    for (const [shipId] of shipEntries(ships)) {
        if (UNFLYABLE_SHIP_IDS.includes(shipId))
            continue;
        const def = getShipDefinition(race, shipId);
        const { speed } = getUpgradedShipStats(def, shipUpgrades[shipId] ?? 0);
        slowest = Math.min(slowest, speed);
    }
    if (!Number.isFinite(slowest))
        return 0;
    return Math.round(slowest * multiplier);
}
/** Total cargo capacity of the manifest (cargo upgrades applied). */
export function calculateFleetCargoCapacity(race, ships, shipUpgrades = {}) {
    let total = 0;
    for (const [shipId, count] of shipEntries(ships)) {
        const def = getShipDefinition(race, shipId);
        total += getUpgradedShipStats(def, shipUpgrades[shipId] ?? 0).cargo * count;
    }
    return total;
}
/** Fleet-wide fuel factor: Σ(count × fuelConsumption). Feeds {@link calculateGasCost}. */
export function calculateFleetFuelConsumption(race, ships) {
    let total = 0;
    for (const [shipId, count] of shipEntries(ships)) {
        total += getShipDefinition(race, shipId).fuelConsumption * count;
    }
    return total;
}
/** Cargo space left after loading (§19). Never negative-clamped — callers reject overloads. */
export function calculateRemainingCargo(capacity, cargo) {
    return capacity - cargo.metal - cargo.minerals - cargo.gas - cargo.scrap;
}
/**
 * Base one-way seconds for a normal (distance-driven) flight:
 * `round(35000 / p × sqrt(d × 10 / fleetSpeed)) + 10`, `p = speedPercent/10`.
 */
export function calculateBaseFlightSeconds(distance, speedPercent, fleetSpeed) {
    if (distance <= 0 || fleetSpeed <= 0 || speedPercent <= 0)
        return 0;
    const p = speedPercent / 10;
    return Math.round((FLIGHT_TIME_FACTOR / p) * Math.sqrt((distance * 10) / fleetSpeed)) + FLIGHT_TIME_BASE_SECONDS;
}
function applyTimeModifiers(seconds, ctx) {
    const flightMultiplier = ctx.flightMultiplier && ctx.flightMultiplier > 0 ? ctx.flightMultiplier : 1;
    let time = Math.round(seconds / flightMultiplier);
    const motivation = ctx.speedMotivationPercent ?? 0;
    if (motivation > 0) {
        time = Math.round(time - (time * motivation) / 100);
    }
    return Math.max(FLEETS_MIN_TIME_SECONDS, time);
}
/**
 * One-way seconds for a mission, dispatching the §16 special cases:
 *
 * - Space Trip: the manually chosen `holdSeconds` IS the one-way time
 *   (round trip = 2×), no distance/speed/modifiers involved.
 * - Same-coordinate Asteroid mining (20 min) / Recycle (15 min)
 *   bases scaled by the selected speed, then normal modifiers.
 * - Recycle to a Sun coordinate: 600 min base scaled by speed, then
 *   normal modifiers.
 * - Everything else: {@link calculateBaseFlightSeconds} + modifiers.
 *
 * Throws for a distance-driven mission whose fleet speed is 0 — dispatch
 * validation must have rejected an unflyable manifest already.
 */
export function calculateMissionFlightSeconds(mission, from, to, speedPercent, fleetSpeed, ctx = {}, holdSeconds = 0) {
    if (mission === "spaceTrip") {
        return Math.max(FLEETS_MIN_TIME_SECONDS, Math.round(holdSeconds));
    }
    const p = speedPercent / 10;
    if (p <= 0)
        throw new Error(`Invalid speedPercent ${speedPercent}`);
    const sameCoordinate = from.galaxy === to.galaxy && from.system === to.system && from.position === to.position;
    const sameCoordinateBase = SAME_COORDINATE_MISSION_BASE_SECONDS[mission];
    if (mission === "recycle" && to.position === 0) {
        return applyTimeModifiers(Math.round(RECYCLE_SUN_BASE_SECONDS / (p / 10)), ctx);
    }
    if (sameCoordinate && sameCoordinateBase !== undefined) {
        return applyTimeModifiers(Math.round(sameCoordinateBase / (p / 10)), ctx);
    }
    const distance = calculateFlightDistance(from, to);
    if (distance <= 0) {
        throw new Error(`Mission "${mission}" cannot fly to its own coordinate`);
    }
    if (fleetSpeed <= 0) {
        throw new Error("Fleet speed is 0 — the manifest contains no flyable ship");
    }
    return applyTimeModifiers(calculateBaseFlightSeconds(distance, speedPercent, fleetSpeed), ctx);
}
// ---------------------------------------------------------------------------
// Gas (§18)
// ---------------------------------------------------------------------------
/**
 * Gas needed for a flight:
 * `round(fuel × d / 35000 × ((p/10)+1)²) + 1`, then reduced by Chemistry
 * (`fuelReduction`, 0-0.75 — 5%/level capped at 75%), then rounded.
 * Space Trip has no distance and uses a flat base cost instead.
 */
export function calculateGasCost(mission, fuelConsumption, distance, speedPercent, fuelReduction = 0) {
    const reduction = Math.min(Math.max(fuelReduction, 0), 0.75);
    if (mission === "spaceTrip") {
        return Math.round(SPACE_TRIP_GAS_COST * (1 - reduction));
    }
    const p = speedPercent / 10;
    const factor = p / 10 + 1;
    const base = Math.round(((fuelConsumption * distance) / FLIGHT_GAS_DISTANCE_DIVISOR) * factor * factor) + 1;
    return Math.round(base * (1 - reduction));
}
// ---------------------------------------------------------------------------
// Flight limits (§2)
// ---------------------------------------------------------------------------
/**
 * Maximum simultaneous non-espionage flights:
 * `ceil(1 + 1.5 × signalSystems + pilotSkill)`. Rounded UP, matching the
 * Signal Systems science description and `getScienceBonus`'s existing
 * `maxSimultaneousFlights` (the flight doc quotes both ceil and floor;
 * ceil was already shipped, so it wins). `pilotSkillLevel` is the Admiral
 * "Pilot" skill — admirals don't exist yet, defaults to 0.
 */
export function calculateMaxActiveFlights(signalSystemsLevel, pilotSkillLevel = 0) {
    const signal = Math.min(Math.max(signalSystemsLevel, 0), 15);
    const pilot = Math.min(Math.max(pilotSkillLevel, 0), 3);
    return Math.ceil(FLIGHT_LIMIT_BASE + FLIGHT_LIMIT_PER_RADAR_LEVEL * signal + pilot);
}
/**
 * Whether a fleet flew from a Team planet (`originOwnerId` is stamped on
 * every one, garrisons flying home included). It takes one of the Team
 * planet's own flight slots, never one of its dispatcher's.
 */
export function isTeamPlanetFlight(fleet) {
    return !!fleet.originOwnerId;
}
/** A player's flight slots in use and available: their own flights only, never the Team planet flights filed under them. */
export function personalFlightCounts(fleets, signalSystemsLevel) {
    const own = fleets.filter((f) => !isTeamPlanetFlight(f));
    return {
        activeCount: own.filter((f) => f.mission !== "espionage").length,
        maxActive: calculateMaxActiveFlights(signalSystemsLevel),
        espionageCount: own.filter((f) => f.mission === "espionage").length,
        maxEspionage: calculateMaxEspionageFlights(signalSystemsLevel),
    };
}
/** Espionage fleets have a separate pool: 5× the normal limit (§2.1). */
export function calculateMaxEspionageFlights(signalSystemsLevel, pilotSkillLevel = 0) {
    return calculateMaxActiveFlights(signalSystemsLevel, pilotSkillLevel) * ESPIONAGE_FLIGHT_LIMIT_MULTIPLIER;
}
/**
 * Why one more flight of this kind can't leave, or `null` (§2, incl. the
 * espionage exception). `activeFleets` are the flights counted against the
 * same slots — a player's own, or a Team planet's.
 */
export function flightLimitBlockReason(activeFleets, signalSystemsLevel, isEspionage) {
    const activeOfKind = activeFleets.filter((f) => (f.mission === "espionage") === isEspionage).length;
    const limit = isEspionage ? calculateMaxEspionageFlights(signalSystemsLevel) : calculateMaxActiveFlights(signalSystemsLevel);
    if (activeOfKind < limit)
        return null;
    return isEspionage
        ? `Espionage flight limit reached (${limit})`
        : `Active flight limit reached (${limit}) — research Signal Systems for more`;
}
// ---------------------------------------------------------------------------
// Recall (§3.1)
// ---------------------------------------------------------------------------
/**
 * Return seconds for a fleet recalled before arrival: exactly the elapsed
 * outbound time (§3.1 — "return_time = elapsed_outbound_travel_time").
 * Gas is NOT reimbursed; that's the caller's non-action.
 */
export function calculateRecallReturnSeconds(departedAt, recalledAt) {
    return Math.max(0, Math.round((recalledAt.getTime() - departedAt.getTime()) / 1000));
}
//# sourceMappingURL=flightEngine.js.map